import {
  CreateStatementDocument,
  DeleteStatementDocument,
  DownloadStatementDocumentDocument,
  StatementDocument,
  StatementsOwnPaginatedDocument,
  StatementsPaginatedDocument,
  UpdateStatementDocument,
  UploadStatementDocumentDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { IFile } from "api/services/types";
import { Optional, PartialNullable, mapInvestorName } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  ICreateStatementVars,
  IStatementDocumentModel,
  IStatementsModel,
  IStatementsPaginatedResponse,
  IStatementsPaginatedVars,
  IUpdateStatementVars,
  StatementCreateUpdateResponse,
} from "./types";

export async function getStatementsPaginated(
  variables: IStatementsPaginatedVars
): Promise<IStatementsPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: StatementsPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return (
      res?.data?.statementsPaginated && {
        ...res.data.statementsPaginated,
        edges: res.data.statementsPaginated.edges.map((item) => ({
          ...item,
          node: {
            ...item.node,
            investorProfile:
              item.node.investorProfile &&
              mapInvestorName(item.node.investorProfile),
          },
        })),
      }
    );
  } catch (error) {
    throw error;
  }
}

export async function getStatement(
  statementId: number
): Promise<Optional<PartialNullable<IStatementsModel>>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: StatementDocument,
      variables: { statementId },
      fetchPolicy: "network-only",
    });

    return res?.data?.statementGetById;
  } catch (error) {
    throw error;
  }
}

export async function createStatement(
  variables: ICreateStatementVars
): Promise<Optional<StatementCreateUpdateResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: CreateStatementDocument,
      variables,
      fetchPolicy: "network-only",
    });

    executeEviction(client, {
      typename: "Statement",
      fieldNames: ["statementsPaginated", "statementsOwnPaginated"],
    });

    return res?.data?.statementCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateStatement(
  variables: IUpdateStatementVars
): Promise<Optional<StatementCreateUpdateResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UpdateStatementDocument,
      variables,
      fetchPolicy: "network-only",
    });

    executeEviction(client, {
      typename: "Statement",
      id: variables.statementId.toString(),
      fieldNames: [
        "statementsPaginated",
        "statementsOwnPaginated",
        "statementGetById",
      ],
    });

    return res?.data?.statementUpdate;
  } catch (error) {
    throw error;
  }
}

export async function deleteStatement(statementId: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: DeleteStatementDocument,
      variables: { statementId },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "Statement",
      id: statementId.toString(),
      fieldNames: ["statementsPaginated", "statementsOwnPaginated"],
    });

    return Boolean(res?.data?.statementDelete);
  } catch (error) {
    throw error;
  }
}

export async function uploadStatementDocument(
  variables: IFile
): Promise<Optional<IStatementDocumentModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UploadStatementDocumentDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    if (variables.relatedEntityId) {
      executeEviction(client, {
        typename: "Statement",
        id: variables.relatedEntityId.toString(),
        fieldNames: [
          "statementGetById",
          "statementsPaginated",
          "statementsOwnPaginated",
        ],
      });
    }

    return res?.data?.statementDocumentUpload;
  } catch (error) {
    throw error;
  }
}

export async function downloadStatementDocument(
  statementDocumentId: number
): Promise<Optional<string>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: DownloadStatementDocumentDocument,
      variables: { statementDocumentId },
      fetchPolicy: "no-cache",
    });

    return res?.data?.statementDocumentDownload;
  } catch (error) {
    throw error;
  }
}

export async function getStatementsPaginatedForInvestor(
  variables: IStatementsPaginatedVars
): Promise<IStatementsPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: StatementsOwnPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.statementsOwnPaginated;
  } catch (error) {
    throw error;
  }
}
